import React, { useState, useCallback, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/** Matches map cell fill max in GenerationProfileChart (legend gradient only). */
const LEGEND_FILL_MAX_OPACITY = 0.85;

function syncWindLayerVisibility(map, layer, visible) {
  if (!map || !layer) return;
  if (visible) {
    if (!map.hasLayer(layer)) layer.addTo(map);
  } else if (map.hasLayer(layer)) {
    map.removeLayer(layer);
  }
}

/** Legend for the simulator grid-cell color scale (hollow → solid red as utilization grows). */
function UtilizationLegend() {
  return (
    <div className="absolute bottom-3 left-3 z-[500] bg-white/90 backdrop-blur-sm rounded-md shadow px-2 py-1.5 pointer-events-none">
      <div className="text-[10px] font-medium text-gray-600 mb-1">Cell utilization</div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-600">Min</span>
        <div
          className="w-24 h-2 rounded border border-gray-200"
          style={{
            background: `linear-gradient(to right, rgba(239,68,68,0) 0%, rgba(239,68,68,${LEGEND_FILL_MAX_OPACITY}) 100%)`
          }}
        />
        <span className="text-[10px] text-gray-600">Max</span>
      </div>
    </div>
  );
}

function SimulatorUtilizationMapsGridInner({
  solarLoading,
  windLoading,
  windMapRef,
  windOnshoreLayerRef,
  windOffshoreLayerRef,
  windOnshoreLayerVisibleRef,
  windOffshoreLayerVisibleRef,
  windMapLayerGeneration,
}) {
  const [windOnshoreLayerVisible, setWindOnshoreLayerVisible] = useState(true);
  const [windOffshoreLayerVisible, setWindOffshoreLayerVisible] = useState(true);

  const handleToggleWindOnshoreLayer = useCallback(() => {
    const next = !windOnshoreLayerVisibleRef.current;
    windOnshoreLayerVisibleRef.current = next;
    setWindOnshoreLayerVisible(next);
    syncWindLayerVisibility(
      windMapRef?.current,
      windOnshoreLayerRef?.current,
      next
    );
  }, [windMapRef, windOnshoreLayerRef, windOnshoreLayerVisibleRef]);

  const handleToggleWindOffshoreLayer = useCallback(() => {
    const next = !windOffshoreLayerVisibleRef.current;
    windOffshoreLayerVisibleRef.current = next;
    setWindOffshoreLayerVisible(next);
    syncWindLayerVisibility(
      windMapRef?.current,
      windOffshoreLayerRef?.current,
      next
    );
  }, [windMapRef, windOffshoreLayerRef, windOffshoreLayerVisibleRef]);

  // Re-apply visibility when parent rebuilds GeoJSON layers (data refresh, not toggle).
  useEffect(() => {
    syncWindLayerVisibility(
      windMapRef?.current,
      windOnshoreLayerRef?.current,
      windOnshoreLayerVisibleRef.current
    );
    syncWindLayerVisibility(
      windMapRef?.current,
      windOffshoreLayerRef?.current,
      windOffshoreLayerVisibleRef.current
    );
  }, [
    windMapLayerGeneration,
    windMapRef,
    windOnshoreLayerRef,
    windOffshoreLayerRef,
    windOnshoreLayerVisibleRef,
    windOffshoreLayerVisibleRef,
  ]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      <div className="min-w-0 bg-white rounded-lg sm:rounded-xl shadow-card hover:shadow-card-hover transition-shadow relative overflow-hidden">
        <h3 className="card-header-figma px-3 sm:px-4 lg:px-6 pt-3 sm:pt-4 pb-3">Simulator Map — Solar Utilization</h3>
        <div id="simulator-map" className="w-full min-w-0 h-[240px] sm:h-[340px] md:h-[380px] lg:h-[420px]" />
        <UtilizationLegend />
        {solarLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <div className="text-gray-600 text-sm">Loading solar map…</div>
          </div>
        )}
      </div>
      <div className="min-w-0 bg-white rounded-lg sm:rounded-xl shadow-card hover:shadow-card-hover transition-shadow relative overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 pt-3 sm:pt-4 pb-3">
          <h3 className="card-header-figma min-w-0">Simulator Map — Wind Utilization</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleToggleWindOnshoreLayer}
              className="flex items-center gap-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors text-xs font-medium"
              title={windOnshoreLayerVisible ? 'Hide Wind Onshore' : 'Show Wind Onshore'}
              aria-label={windOnshoreLayerVisible ? 'Hide Wind Onshore' : 'Show Wind Onshore'}
            >
              {windOnshoreLayerVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              <span>Onshore</span>
            </button>
            <button
              type="button"
              onClick={handleToggleWindOffshoreLayer}
              className="flex items-center gap-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors text-xs font-medium"
              title={windOffshoreLayerVisible ? 'Hide Wind Offshore' : 'Show Wind Offshore'}
              aria-label={windOffshoreLayerVisible ? 'Hide Wind Offshore' : 'Show Wind Offshore'}
            >
              {windOffshoreLayerVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              <span>Offshore</span>
            </button>
          </div>
        </div>
        <div id="simulator-map-wind" className="w-full min-w-0 h-[240px] sm:h-[340px] md:h-[380px] lg:h-[420px]" />
        <UtilizationLegend />
        {windLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <div className="text-gray-600 text-sm">Loading wind map…</div>
          </div>
        )}
      </div>
    </div>
  );
}

const SimulatorUtilizationMapsGrid = React.memo(SimulatorUtilizationMapsGridInner);
SimulatorUtilizationMapsGrid.displayName = 'SimulatorUtilizationMapsGrid';

export default SimulatorUtilizationMapsGrid;
